import { useEffect, useMemo, useSyncExternalStore } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import stripHTMLTags from "../functions";
import BASE from "../config";

const DEFAULT_DESCRIPTION =
  "Book luxury holiday villas and apartments in the Algarve with Sunbound Villas. Hand-picked stays in Vale do Lobo, Quinta do Lago, Dunas Douradas and nearby resorts.";
const DEFAULT_KEYWORDS =
  "Algarve villas, holiday rentals Algarve, Vale do Lobo villas, Quinta do Lago villas, Dunas Douradas, luxury villa rental Portugal, Sunbound Villas";
const DEFAULT_TITLE =
  "Sunbound Villas | Luxury Holiday Villas & Apartments in the Algarve";

function cleanText(value, maxLength) {
  if (!value) return "";
  const text = stripHTMLTags(String(value))
    .replace(/&nbsp;/g, " ")
    .replace(/&euro;/g, "€")
    .replace(/&rsquo;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
  if (!maxLength || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

export function canonicalFromPath(pathname) {
  const path = pathname === "/" ? "/" : String(pathname || "/").replace(/\/+$/, "") || "/";
  return `${BASE.LIVE_SITE_URL}${path}`;
}

let pageSeo = null;
const seoListeners = new Set();

function subscribeSeo(listener) {
  seoListeners.add(listener);
  return () => seoListeners.delete(listener);
}

function getPageSeo() {
  return pageSeo;
}

function setPageSeo(next) {
  pageSeo = next;
  seoListeners.forEach((listener) => listener());
}

function useRegisterPageSeo(value) {
  useEffect(() => {
    setPageSeo(value);
    return () => setPageSeo(null);
  }, [value]);
}

/**
 * Single Helmet for the whole app so title/description/keywords/canonical
 * exist once. Page components register overrides via PropertySeo / ResortSeo.
 */
export function DefaultSeo() {
  const { pathname } = useLocation();
  const override = useSyncExternalStore(subscribeSeo, getPageSeo, getPageSeo);

  return (
    <Helmet prioritizeSeoTags>
      <title>{override?.title || DEFAULT_TITLE}</title>
      <meta name="description" content={override?.description || DEFAULT_DESCRIPTION} />
      <meta name="keywords" content={override?.keywords || DEFAULT_KEYWORDS} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={override?.canonical || canonicalFromPath(pathname)} />
    </Helmet>
  );
}

export function PropertySeo({ property }) {
  const { pathname } = useLocation();

  const value = useMemo(() => {
    if (!property?.id) {
      return { canonical: canonicalFromPath(pathname) };
    }

    const resortTitle = property.resorts?.title || "";
    const name = property.property_name || property.property_name_2 || "Villa";
    const title = resortTitle
      ? `${name} | ${resortTitle}, Algarve | Sunbound Villas`
      : `${name} | Algarve | Sunbound Villas`;
    const description =
      cleanText(property.meta_description, 160) ||
      cleanText(property.description, 160) ||
      DEFAULT_DESCRIPTION;
    const keywords =
      cleanText(property.meta_keywords) ||
      [name, resortTitle, "Algarve villa", "holiday rental", "Sunbound Villas"]
        .filter(Boolean)
        .join(", ");

    return {
      title,
      description,
      keywords,
      canonical: canonicalFromPath(pathname),
    };
  }, [property, pathname]);

  useRegisterPageSeo(value);
  return null;
}

export function ResortSeo({ resort }) {
  const { pathname } = useLocation();

  const value = useMemo(() => {
    if (!resort?.id) {
      return { canonical: canonicalFromPath(pathname) };
    }

    return {
      title: `${resort.title} Villas | Algarve | Sunbound Villas`,
      description:
        cleanText(resort.meta_description, 160) ||
        cleanText(resort.description, 160) ||
        DEFAULT_DESCRIPTION,
      keywords:
        cleanText(resort.meta_keywords) ||
        `${resort.title}, Algarve villas, holiday rentals, Sunbound Villas`,
      canonical: canonicalFromPath(pathname),
    };
  }, [resort, pathname]);

  useRegisterPageSeo(value);
  return null;
}

export { cleanText };
